import React, { useState, useEffect } from "react";
import SearchResults from "./SearchResults";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Box, CircularProgress } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import Utils from "../Utils";
import { ApplicationDataAPI } from "../API/ApplicationDataAPI";

const useStyles = makeStyles((theme) => ({
	formControl: {
		margin: theme.spacing(1),
		minWidth: 240,
		display: "block",
	},
	selectEmpty: {
		marginTop: theme.spacing(2),
	},
	selectField: {
		width: "240px",
	},
	searchField: {
		width: "240px",
		marginLeft: "40px",
	},
	searchButton: {
		marginLeft: "10px",
		height: "50px",
		textTransform: "capitalize",
	},
	table: {
		"& th": {
			padding: "1rem 2rem",
		},
		"& td": {
			padding: "1rem 2rem",
			borderRight: "1px solid rgba(224, 224, 224, 1)",
		},
	},
}));

export default function SearchModal(props) {
	const {
		open,
		onClose,
		searchString,
		setSearchString,
		appResults,
		setAppResults,
		isLoading,
		setIsLoading,
		isStandAlone,
	} = props;
	const classes = useStyles();
	const [searchCriteria, setSearchCriteria] = useState("By Application Id");
	const [searchInput, setSearchInput] = useState(searchString);
	const handleClose = () => {
		setSearchCriteria("By Application Id");
		setSearchInput("");
		setSearchString("");
		setAppResults([]);
		onClose();
	};

	useEffect(() => {
		if (
			searchString !== "" &&
			(searchInput === undefined || searchInput === "")
		) {
			setSearchInput(searchString);
		}
	}, [appResults, searchString]);

	const handleAppSearchByAPI = async (searchStringData, searchCriteria) => {
		try {
			setIsLoading(true);
			let searchObj = {};
			let searchString = searchStringData.trim();
			switch (searchCriteria) {
				case "By Application Id":
					searchObj = { id: searchString };
					break;
				case "By Applicant":
					searchObj = { applicant_name: searchString };
					break;
				case "By Broker":
					searchObj = { broker_account_name: searchString };
					break;
				case "By Contract Number":
					searchObj = { contract_number: searchString };
					break;
				case "By Guarantor":
					searchObj = { guarantor_name: searchString };
					break;
				case "By Vendor":
					searchObj = { vendor_name: searchString };
					break;
				case "By VIN/Serial":
					searchObj = { vin: searchString };
					break;
				case "By SSN":
					searchObj = { lessee_fein: searchString };
					break;
				case "By Referral Party App Id":
					searchObj = { referral_party_application_id: searchString };
					break;
				default:
					searchObj = {};
					return;
			}
			let applications;
			if (searchCriteria === "By SSN") {
				let appsSsn = await ApplicationDataAPI.searchApplications(searchObj, "ALL");
				let appsGuarSSN = await ApplicationDataAPI.searchApplications(
					{
						action: "app_guarantor_fein",
						app_guarantor_fein: searchString,
					},
					"ALL"
				);
				applications = [...appsSsn, ...appsGuarSSN];
			} else {
				applications = await ApplicationDataAPI.searchApplications(searchObj, "ALL");
			}

			//limit the data to only 100 results
			if (applications.length >= 100) {
				applications.length = 100;
			}

			if (applications) {
				setAppResults(applications);
				setSearchString("");
				setIsLoading(false);
			}
		} catch (ex) {
			setAppResults([]);
			setIsLoading(false);
		}
	};

	return (
		<Dialog
			fullWidth
			maxWidth="xl"
			open={open}
			onClose={handleClose}>
			<DialogTitle>Application Search</DialogTitle>
			<DialogContent>
				<Box className={classes.formRow}>
					<FormControl variant="outlined" className={classes.selectControl}>
						<InputLabel id="app-search-criteria-label">Search Criteria</InputLabel>
						<Select
							labelId="app-search-criteria-label"
							onChange={(event) => setSearchCriteria(event.target.value)}
							label="Search Criteria"
							value={searchCriteria}>
							<MenuItem value={"By Application Id"}>By Application Id</MenuItem>
							<MenuItem value={"By Applicant"}>By Applicant</MenuItem>
							<MenuItem value={"By Broker"}>By Broker</MenuItem>
							<MenuItem value={"By Contract Number"}>By Contract Number</MenuItem>
							<MenuItem value={"By Guarantor"}>By Guarantor</MenuItem>
							<MenuItem value={"By Vendor"}>By Vendor</MenuItem>
							<MenuItem value={"By VIN/Serial"}>By VIN/Serial</MenuItem>
							<MenuItem value={"By SSN"}>By SSN</MenuItem>
							<MenuItem value={"By Referral Party App Id"}>
								By Referral Party App Id
							</MenuItem>
						</Select>
					</FormControl>
					<TextField
						className={classes.searchField}
						label="Search Field"
						type="search"
						variant="outlined"
						value={searchInput}
						placeholder={searchCriteria || "By Application Id"}
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyUp={(event) => {
							if (event.key === "Enter" && searchInput.length >= 3) {
								handleAppSearchByAPI(searchInput, searchCriteria);
							}
						}}
					/>
					<Button
						variant="contained"
						color="primary"
						className={classes.searchButton}
						endIcon={<SearchIcon />}
						disabled={isLoading || searchInput.length < 3}
						onClick={() => handleAppSearchByAPI(searchInput, searchCriteria)}>
						Search
					</Button>
				</Box>

				{isLoading ? (
					<Box
						style={{
							fontWeight: "bold",
							padding: "0.5rem",
							margin: "0.5rem",
							border: "1px solid",
							borderRadius: "4px",
						}}
						align="center">
						<CircularProgress />
					</Box>
				) : appResults?.length === 0 ? (
					<Box
						style={{
							fontWeight: "bold",
							padding: "0.5rem",
							margin: "0.5rem",
							border: "1px solid",
							borderRadius: "4px",
						}}
						align="center">
						<span>No Records Found</span>
					</Box>
				) : (
					<SearchResults
						applications={appResults}
						isStandAlone={isStandAlone}
						onClose={handleClose}
					/>
				)}
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => {
						setSearchCriteria("By Application Id");
						setSearchInput("");
						setSearchString("");
						setAppResults([]);
						onClose();
					}}
					style={{ textTransform: "capitalize" }}
					color="primary">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
}
